import React from 'react';
import { clsx } from 'clsx';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helpText?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  variant?: 'default' | 'filled' | 'outlined';
  fullWidth?: boolean;
}

const Input: React.FC<InputProps> = ({
  label,
  error,
  helpText,
  icon,
  iconPosition = 'left',
  variant = 'default',
  fullWidth = true,
  className,
  id,
  ...props
}) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  
  const baseClasses = 'block px-2.5 py-1.5 text-sm transition-colors duration-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-avi-primary/50';
  
  const variants = {
    default: 'border border-gray-300 rounded-lg bg-white focus:border-avi-primary',
    filled: 'border-0 rounded-lg bg-gray-100 focus:bg-white focus:ring-2',
    outlined: 'border-2 border-gray-300 rounded-lg bg-white focus:border-avi-primary',
  };

  const errorClasses = error ? 'border-avi-danger focus:border-avi-danger focus:ring-avi-danger/50' : '';
  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label htmlFor={inputId} className="block text-xs font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      
      <div className="relative">
        {icon && iconPosition === 'left' && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <div className="text-gray-400">{icon}</div>
          </div>
        )}
        
        <input
          id={inputId}
          className={clsx(
            baseClasses,
            variants[variant],
            errorClasses,
            widthClass,
            icon && iconPosition === 'left' ? 'pl-10' : '',
            icon && iconPosition === 'right' ? 'pr-10' : '',
            className
          )}
          {...props}
        />
        
        {icon && iconPosition === 'right' && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <div className="text-gray-400">{icon}</div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-xs text-avi-danger">{error}</p>
      )}
      
      {helpText && !error && (
        <p className="mt-1 text-xs text-gray-500">{helpText}</p>
      )}
    </div>
  );
};

export default Input;